/**
 * nav — pure, deterministic page-hierarchy helpers shared by the editor
 * (apps/builder-webapp) and the published-site renderer (apps/workerd-runtime).
 *
 * Edges are the site's navigation primitives AND its hierarchy: a node's
 * PARENT is the source of its incoming edge; "B is a child of A" = edge
 * {source:A, target:B}.
 *
 * Both consumers MUST agree on parent selection or the editor and the rendered
 * nav diverge — so the single source of truth lives here. A node may have more
 * than one incoming edge (manual links, imported/corrupt docs); selection is
 * therefore DETERMINISTIC and **order-independent**: incoming edges are sorted
 * by a stable key `(menuPosition ?? MAX, source, id)` and the first wins. Never
 * rely on edge array order (workerd has a determinism test that shuffles it).
 */

/** Minimal edge shape these helpers operate on (the full `Edge` is a superset). */
export interface NavEdge {
  readonly id: string;
  readonly source: string;
  readonly target: string;
  // `| undefined` (not just optional) to accept the schema Edge type under
  // exactOptionalPropertyTypes, where optional fields are `T | undefined`.
  readonly menuPosition?: number | undefined;
  readonly hidden?: boolean | undefined;
}

const POS_MAX = Number.MAX_SAFE_INTEGER;

/** Stable ordering for picking one parent among multiple incoming edges. */
export function navIncomingSort(a: NavEdge, b: NavEdge): number {
  const pa = a.menuPosition ?? POS_MAX;
  const pb = b.menuPosition ?? POS_MAX;
  if (pa !== pb) return pa - pb;
  if (a.source !== b.source) return a.source < b.source ? -1 : 1;
  return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
}

/**
 * The single, deterministic parent id of `childId`, or null. `visibleOnly`
 * ignores hidden edges (a hidden link doesn't count as a parent → the child
 * promotes in the rendered nav).
 */
export function navParentOf(
  edges: readonly NavEdge[],
  childId: string,
  options?: { visibleOnly?: boolean },
): string | null {
  const incoming = edges.filter(
    (e) =>
      e.target === childId &&
      e.source !== childId &&
      !(options?.visibleOnly && e.hidden),
  );
  if (incoming.length === 0) return null;
  return [...incoming].sort(navIncomingSort)[0]!.source;
}

/**
 * Direct children of `parentId`, deduped and ordered by `navIncomingSort`.
 * Only nodes whose DETERMINISTIC parent is `parentId` are included, so a node
 * with several incoming edges appears under exactly one parent.
 */
export function navChildrenOf(
  edges: readonly NavEdge[],
  parentId: string,
  options?: { visibleOnly?: boolean },
): string[] {
  const child = new Map<string, NavEdge>();
  for (const e of edges) {
    if (e.source !== parentId || e.target === parentId) continue;
    if (options?.visibleOnly && e.hidden) continue;
    if (navParentOf(edges, e.target, options) !== parentId) continue;
    const prev = child.get(e.target);
    if (!prev || navIncomingSort(e, prev) < 0) child.set(e.target, e);
  }
  return [...child.values()].sort(navIncomingSort).map((e) => e.target);
}

/** All transitive descendant ids of `rootId` (cycle-safe; excludes root). */
export function navDescendantIds(
  edges: readonly NavEdge[],
  rootId: string,
): Set<string> {
  const out = new Set<string>();
  const stack = [rootId];
  while (stack.length) {
    const cur = stack.pop()!;
    for (const e of edges) {
      if (e.source !== cur || e.target === rootId || out.has(e.target))
        continue;
      out.add(e.target);
      stack.push(e.target);
    }
  }
  return out;
}
